import React, { useState, useEffect } from 'react';
import '../style/PromoteContent.css';
import '../style/PaymentModal.css';

import { Link } from 'react-router-dom';

import phoneIcon from '../../../assets/icons/phone.svg';
import eyeIcon from '../../../assets/icons/eye.svg';
import doneIcon from '../../../assets/icons/done.svg';
import chatIcon from '../../../assets/icons/chat.svg';
import heartIcon from '../../../assets/icons/heart.svg';
import infoIcon from '../../../assets/icons/info.svg';

import { getUserInfo } from '../../../api/usercall';
import { postPromote } from '../../../api/postPromote';
import { getPromotionInfo } from '../../../api/getPromotionInfo';
import { getPromotionPackages } from '../../../api/getPromotionPackages';

import min_icon from '../../../assets/icons/min_flash.svg';
import max_icon from '../../../assets/icons/max_flash.svg';
import opt_icon from '../../../assets/icons/opt_flash.svg';

import warning_icon from '../../../assets/icons/WarningCircle.svg';

import { t } from 'i18next';
import i18n from '../../../language/i18n';

const PromoteContent = ({ data }) => {
    const [userBalance, setUserBalance] = useState(0);
    const [activatedPackages, setActivatedPackages] = useState({});
    const [activatedServices, setActivatedServices] = useState({});
    const [promotionInfo, setPromotionInfo] = useState(null);
    const [isLoadingPromotion, setIsLoadingPromotion] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [promotionPackages, setPromotionPackages] = useState([]);
    const [isLoadingPackages, setIsLoadingPackages] = useState(true);

    // Format time remaining
    const formatTimeRemaining = (seconds) => {
        if (seconds === null || seconds === undefined || seconds <= 0) {
            return t('unlimited') || 'Шексіз';
        }
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        
        if (days > 0) {
            return `${days} ${t('days') || 'күн'}`;
        } else if (hours > 0) {
            return `${hours} ${t('hours') || 'сағат'}`;
        } else {
            return `${minutes} ${t('minutes') || 'минут'}`;
        }
    };

    // Update time remaining every second
    useEffect(() => {
        if (!promotionInfo || !promotionInfo.remaining_time_seconds) return;

        setTimeRemaining(promotionInfo.remaining_time_seconds);

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [promotionInfo]);

    // Helper function to get API language code
    const getApiLanguageCode = () => {
        const currentLang = i18n.language || 'kz';
        // Map 'kz' to 'kk' for API
        return currentLang === 'kz' ? 'kk' : currentLang;
    };

    // Helper function to get localized field from package
    const getLocalizedField = (packageItem, fieldName) => {
        const lang = getApiLanguageCode();
        const fieldKey = `${fieldName}_${lang}`;
        // Fallback to Russian if localized field doesn't exist
        return packageItem[fieldKey] || packageItem[fieldName] || '';
    };

    // Helper function to map package id to promotionType index
    const getPromotionTypeIndex = (packageId) => {
        const mapping = {
            'minimal': 0,
            'optimal': 1,
            'maximal': 2
        };
        return mapping[packageId] !== undefined ? mapping[packageId] : 0;
    };

    // Helper function to get icon for package
    const getPackageIcon = (packageId) => {
        const iconMapping = {
            'minimal': min_icon,
            'optimal': opt_icon,
            'maximal': max_icon
        };
        return iconMapping[packageId] || min_icon;
    };

    useEffect(() => {
        const fetchUserBalance = async () => {
            try {
                const response = await getUserInfo();
                setUserBalance(response.user.balance);
            } catch (error) {
                console.error("Failed to fetch user balance:", error);
            }
        };
        fetchUserBalance();
    }, []);

    useEffect(() => {
        const fetchPromotionPackages = async () => {
            setIsLoadingPackages(true);
            try {
                const packages = await getPromotionPackages();
                // Filter only active packages
                const activePackages = packages.filter(pkg => pkg.is_active);
                setPromotionPackages(activePackages);
            } catch (error) {
                console.error("Failed to fetch promotion packages:", error);
                setPromotionPackages([]);
            } finally {
                setIsLoadingPackages(false);
            }
        };
        fetchPromotionPackages();
    }, []);

    useEffect(() => {
        const fetchPromotionInfo = async () => {
            if (!data?.id) return;
            
            setIsLoadingPromotion(true);
            try {
                const response = await getPromotionInfo(data.id);
                console.log('Promotion info loaded:', response);
                
                // Handle nested response structure: { has_promotion: true, promotion: {...} }
                let info = null;
                if (response) {
                    // Check if response has nested promotion object
                    if (response.promotion) {
                        info = response.promotion;
                    }
                    // If it's a direct promotion object (has id or package_type at root level)
                    else if (response.id || response.package_type) {
                        info = response;
                    }
                }
                
                console.log('Extracted promotion info:', info);
                console.log('Package type:', info?.package_type);
                console.log('Status:', info?.status);
                
                if (info) {
                    setPromotionInfo(info);
                } else {
                    setPromotionInfo(null);
                }
            } catch (error) {
                console.error("Failed to fetch promotion info:", error);
                setPromotionInfo(null);
            } finally {
                setIsLoadingPromotion(false);
            }
        };
        
        fetchPromotionInfo();
    }, [data?.id]);

    const handleActivateClick = async (id, item, type) => {
        // Check if there's an active or pending promotion
        if (promotionInfo && (promotionInfo.status === 'active' || promotionInfo.status === 'pending')) {
            const message = promotionInfo.status === 'active' 
                ? (t('activePromotionExists') || "Жарнамада белсенді промо бар. Алдыңғы промо біткенге дейін жаңасын құру мүмкін емес.")
                : (t('promotionPending') || "Промо белсендіру процесінде. Күте тұрыңыз.");
            alert(message);
            return;
        }

        if (userBalance < item.units) {
            alert(t('notEnoughUnits') || `Баланста ${t('units') || 'бірлік'} жеткіліксіз!`);
            return;
        }

        if ((type === "package" && activatedPackages[item.title]) ||
            (type === "service" && activatedServices[item.name])) {
            alert(t('alreadyActivated') || "Бұл тариф/қызмет бұрыннан белсенді!");
            return;
        }

        try {
            const promotionPackageIndex = item.promotionType;
            const response = await postPromote(id, promotionPackageIndex);

            if (response.promotion_id) {
                if (type === "package") {
                    setActivatedPackages((prev) => ({ ...prev, [item.title]: true }));
                } else {
                    setActivatedServices((prev) => ({ ...prev, [item.name]: true }));
                }
                setUserBalance((prevBalance) => prevBalance - item.units);

                // Refresh promotion info after successful activation
                const updatedInfo = await getPromotionInfo(id);
                setPromotionInfo(updatedInfo);

                alert(t('promotionActivated') || "Тариф сәтті белсендірілді!");
            } else {
                alert(t('activationFailed') || "Тарифті белсендіру мүмкін болмады. Қайталап көріңіз.");
            }
        } catch (error) {
            console.error("Ошибка при активации:", error);
            const errorMessage = error.message || (t('activationError') || "Қате орын алды. Кейінірек қайталап көріңіз.");
            alert(errorMessage);
        }
    };

    const getPackageName = (packageType) => {
        if (!packageType) return 'Белгісіз';
        const packageNames = {
            'minimal': t('minimalTitle') || 'Минимал',
            'optimal': t('optimalTitle') || 'Оптимал',
            'maximal': t('maximalTitle') || 'Максимал'
        };
        const name = packageNames[packageType];
        return name || packageType || 'Белгісіз';
    };

    const getStatusText = (status) => {
        if (!status) return 'Белгісіз';
        const statusTexts = {
            'pending': t('promotionStatusPending') || 'Күтуде',
            'active': t('promotionStatusActive') || 'Белсенді',
            'expired': t('promotionStatusExpired') || 'Жарамдылығы бітті'
        };
        const text = statusTexts[status];
        return text || status || 'Белгісіз';
    };

    return (
        <div className="promo_container">
            <div className="promo_content_left_section content_background">
                <div className="item-card">
                    <div className="promo_item_details">
                        <div className="promo_item_details_content">
                            <img
                                src={data.main_image_url}
                                alt="Item"
                                className="promo_item_details_content_image"
                            />
                            <div>
                                <h3 className="promo_item_details_content_title">{data.title}</h3>
                                <p className="promo_item_details_content_price">{data.price} тг.</p>
                            </div>
                        </div>
                        <div className="profile_advertisements_info">
                            <div className="info_block">
                                <img src={phoneIcon} alt="Phone" />
                                <span>{data.calls_count}</span>
                            </div>
                            <div className="info_block">
                                <img src={eyeIcon} alt="Views" />
                                <span>{data.views_count}</span>
                            </div>
                            <div className="info_block">
                                <img src={heartIcon} alt="Likes" />
                                <span>{data.favorites_count}</span>
                            </div>
                            <div className="info_block">
                                <img src={chatIcon} alt="Comments" />
                                <span>{data.messages_count}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="promo_content_right_section">
                <div className="balance_card content_background">
                    <p>{t('yourBalance')}: <strong className="balance">{userBalance} {t('units')}</strong></p>
                    <div className="balance_card_second_text">
                        <img src={infoIcon} alt="info" />
                        <p>{t('promoteText')}</p>
                    </div>
                </div>

                {/* Current Promotion Info */}
                {isLoadingPromotion && (
                    <div className="content_background" style={{ marginTop: '20px', padding: '20px', textAlign: 'center' }}>
                        <p>{t('loading') || 'Жүктелуде...'}</p>
                    </div>
                )}
                {!isLoadingPromotion && promotionInfo && (
                    <div className="content_background" style={{ marginTop: '20px', padding: '20px' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
                            {t('currentPromotion') || 'Ағымдағы промо'}
                        </h3>
                        <div style={{ 
                            padding: '15px', 
                            backgroundColor: promotionInfo.status === 'active' ? '#DCECDF' : '#FFF3CD',
                            borderRadius: '10px',
                            border: `2px solid ${promotionInfo.status === 'active' ? '#4CAF50' : '#FFC107'}`
                        }}>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>{t('packageType') || 'Пакет түрі'}: </strong>
                                <span>{promotionInfo?.package_type ? getPackageName(promotionInfo.package_type) : 'Белгісіз'}</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <strong>{t('status') || 'Статус'}: </strong>
                                <span style={{ 
                                    padding: '4px 8px', 
                                    borderRadius: '4px',
                                    backgroundColor: promotionInfo.status === 'active' ? '#4CAF50' : promotionInfo.status === 'pending' ? '#2196F3' : '#9E9E9E',
                                    color: 'white',
                                    fontSize: '12px',
                                    marginLeft: '8px'
                                }}>
                                    {promotionInfo?.status ? getStatusText(promotionInfo.status) : 'Белгісіз'}
                                </span>
                            </div>
                            {promotionInfo.status === 'active' && (
                                <>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>{t('viewsProgress') || 'Көру прогрессі'}: </strong>
                                        {promotionInfo.current_views || 0} / {promotionInfo.max_views || 0}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ 
                                            width: '100%', 
                                            backgroundColor: '#E0E0E0', 
                                            borderRadius: '10px', 
                                            height: '20px',
                                            overflow: 'hidden',
                                            marginTop: '5px'
                                        }}>
                                            <div style={{ 
                                                width: `${promotionInfo.progress_percentage || 0}%`, 
                                                backgroundColor: '#4CAF50', 
                                                height: '100%',
                                                transition: 'width 0.3s ease'
                                            }} />
                                        </div>
                                        <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                            {promotionInfo.progress_percentage || 0}% {t('completed') || 'аяқталды'}
                                        </small>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>{t('remainingViews') || 'Қалған көру саны'}: </strong>
                                        {promotionInfo.remaining_views || 0}
                                    </div>
                                    {promotionInfo.remaining_time_seconds !== null && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>{t('timeRemaining') || 'Қалған уақыт'}: </strong>
                                            {timeRemaining !== null ? formatTimeRemaining(timeRemaining) : formatTimeRemaining(promotionInfo.remaining_time_seconds)}
                                        </div>
                                    )}
                                    {promotionInfo.expires_at && (
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                            {t('expiresAt') || 'Жарамдылығы бітеді'}: {new Date(promotionInfo.expires_at).toLocaleString()}
                                        </div>
                                    )}
                                    <div className="balance-warning" style={{ marginTop: '15px', backgroundColor: '#FFF3CD', border: '1px solid #FFC107' }}>
                                        <img src={warning_icon} alt="warning" style={{ width: '20px', height: '20px' }} />
                                        <p>
                                            {t('activePromotionWarning') || 'Жарнамада белсенді промо бар. Алдыңғы промо біткенге дейін жаңасын құру мүмкін емес.'}
                                        </p>
                                    </div>
                                </>
                            )}
                            {promotionInfo.status === 'pending' && (
                                <div style={{ color: '#666', fontSize: '14px' }}>
                                    {t('promotionPending') || 'Промо белсендіру процесінде'}
                                </div>
                            )}
                            {promotionInfo.status === 'expired' && (
                                <div style={{ color: '#666', fontSize: '14px' }}>
                                    {t('promotionExpired') || 'Промо жарамдылығы бітті. Жаңа промо құра аласыз.'}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="services-section">
                    <h2 className="content_background">{t('promoteTitle')}</h2>
                    <div className="services">
                        {isLoadingPackages ? (
                            <div className="content_background" style={{ padding: '20px', textAlign: 'center' }}>
                                <p>{t('loading') || 'Жүктелуде...'}</p>
                            </div>
                        ) : promotionPackages.length === 0 ? (
                            <div className="content_background" style={{ padding: '20px', textAlign: 'center' }}>
                                <p>{t('noData') || 'Деректер жоқ'}</p>
                            </div>
                        ) : promotionPackages.map((packageItem, index) => {
                            const service = {
                                id: packageItem.id,
                                name: getLocalizedField(packageItem, 'title'),
                                iconPath: getPackageIcon(packageItem.id),
                                units: packageItem.units,
                                price: `${packageItem.price} ${packageItem.currency || 'тг.'}`,
                                promotionType: getPromotionTypeIndex(packageItem.id),
                                durationDescription: getLocalizedField(packageItem, 'duration_description'),
                                key: packageItem.id,
                                isPopular: false, // API doesn't provide is_popular for packages
                            };
                            return (
                            <div key={service.id} className={`content_background service_card ${activatedServices[service.name] ? 'active' : ''}`}>
                                <div>
                                    <h3 className="tariff_title">
                                        <img src={service.iconPath} alt='icon' />
                                        {service.name}
                                        {service.isPopular && <span className="popular_badge">{t('popular')}</span>}
                                    </h3>
                                    {service.durationDescription && (
                                        <div className="package_card_info">
                                            <img src={doneIcon} alt="done" />
                                            <p>{service.durationDescription}</p>
                                        </div>
                                    )}
                                    <div className="package_card_info">
                                        <img src={doneIcon} alt="done" />
                                        <p>{service.units} {t('units')}</p>
                                    </div>
                                    <div className="package_card_info">
                                        <img src={doneIcon} alt="done" />
                                        <p>{service.price}</p>
                                    </div>
                                </div>
                                {userBalance < service.units && (
                                    <div className="balance-warning">
                                        <img src={warning_icon} alt={'warning'} />
                                        <p>
                                            {t('notEnoughCredits')}{' '}
                                            <Link to="/profile#walletTopUp" className="topup-link">
                                                {t('topupBalance')}
                                            </Link>
                                        </p>
                                    </div>
                                )}
                                <button
                                    disabled={
                                        isLoadingPromotion ||
                                        userBalance < service.units || 
                                        (promotionInfo && (promotionInfo.status === 'active' || promotionInfo.status === 'pending'))
                                    }
                                    className={`promo_button ${
                                        isLoadingPromotion ||
                                        userBalance < service.units || 
                                        (promotionInfo && (promotionInfo.status === 'active' || promotionInfo.status === 'pending'))
                                            ? 'error' 
                                            : ''
                                    } ${activatedServices[service.name] ? 'active' : ''}`}
                                    onClick={() => handleActivateClick(data.id, service, 'package')}
                                >
                                    {isLoadingPromotion
                                        ? (t('loading') || 'Жүктелуде...')
                                        : promotionInfo && promotionInfo.status === 'active' 
                                        ? (t('activePromotionBlocked') || 'Белсенді промо бар')
                                        : promotionInfo && promotionInfo.status === 'pending'
                                        ? (t('promotionPending') || 'Күтуде')
                                        : activatedServices[service.name] 
                                            ? t('activated') 
                                            : t('activate')}
                                </button>
                            </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromoteContent;